/**
 * PostHog configuration per deployment variant (China web, global web, app WebView).
 * Uses POSTHOG_API_KEY for web; app WebView always uses the global PostHog project.
 */
import config from "@Config/index";
import getCookie from "helpers/getCookie";
import { appOS, isApp } from "hooks/useIsApp";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";

export type PostHogSource = "amber_china_website" | "amber_global_website" | "amber_app";

export type PostHogRuntimeConfig = {
  apiKey: string;
  host: string;
  source: PostHogSource;
};

const POSTHOG_CHINA_API_HOST = "https://tracking.amberstudent.cn";
const POSTHOG_GLOBAL_API_HOST = "https://posthog.amberstudent.com";

/** Global PostHog project key (app WebView + global web fallback). */
export const POSTHOG_GLOBAL_PROJECT_KEY = "phc_vCbyBuK48hfdALfigRCiaLunweodmY66WAput3j3zNhM";

export const getPostHogConfig = (): PostHogRuntimeConfig => {
  if (isApp()) {
    return {
      apiKey: POSTHOG_GLOBAL_PROJECT_KEY,
      host: POSTHOG_GLOBAL_API_HOST,
      source: "amber_app",
    };
  }

  if (config.IS_CHINA_APP) {
    return {
      apiKey: config.POSTHOG_API_KEY,
      host: POSTHOG_CHINA_API_HOST,
      source: "amber_china_website",
    };
  }

  return {
    apiKey: config.POSTHOG_API_KEY,
    host: POSTHOG_GLOBAL_API_HOST,
    source: "amber_global_website",
  };
};

const DESTINATION_COUNTRY_STORAGE_KEY = "destinationCountry";
const LAST_VISITED_PROPERTY_STORAGE_KEY = "lastVisitedProperty";
const LAST_VISITED_PROPERTY_AT_STORAGE_KEY = "lastVisitedPropertyAt";
const LAST_SEARCHED_REGION_STORAGE_KEY = "lastSearchedRegion";
const LAST_SEARCHED_REGION_AT_STORAGE_KEY = "lastSearchedRegionAt";
const HAS_SHORTLISTED_STORAGE_KEY = "hasShortlisted";
const LEAD_FORM_OPENED_AT_STORAGE_KEY = "leadFormOpenedAt";
const LAST_VISITED_PROPERTY_NAME_STORAGE_KEY = "lastVisitedPropertyName";
const LAST_SEARCHED_REGION_NAME_STORAGE_KEY = "lastSearchedRegionName";
const LAST_APPLIED_FILTER_STORAGE_KEY = "lastAppliedFilter";
const LAST_APPLIED_FILTER_AT_STORAGE_KEY = "lastAppliedFilterAt";
const LAST_ACTIVE_AT_STORAGE_KEY = "lastActiveAt";

/** ISO country from api.country.is (localStorage/cookie populated by fetchCountryData). */
export const getUserCountryForPostHog = (): string => {
  if (typeof window === "undefined") return "";
  return readStored("country") || getCookie("userCountry") || "";
};

/** Destination country (search/property region), persisted from appData.countryCode. */
export const setDestinationCountryForPostHog = (countryCode?: string): void => {
  if (typeof window === "undefined" || !countryCode) return;
  writeStored(DESTINATION_COUNTRY_STORAGE_KEY, countryCode);
};

export const getDestinationCountryForPostHog = (): string => {
  if (typeof window === "undefined") return "";
  return readStored(DESTINATION_COUNTRY_STORAGE_KEY);
};

/**
 * localStorage that cannot take a render down with it.
 *
 * Every writer below is called from inside a React effect, and `setItem`
 * throws `QuotaExceededError` when site data is blocked or full — Safari
 * private browsing being the usual way to meet it. An analytics property
 * failing to save is not a reason for the route effect that called it to stop
 * halfway, so the failure stays here.
 */
const readStored = (key: string): string => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStored = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked or full — the person property is simply not recorded.
  }
};

/** Slug + the moment it changed, so a re-view does not keep moving the timestamp. */
const setTimestampedValue = (key: string, atKey: string, value: string): void => {
  if (typeof window === "undefined" || !value) return;
  if (readStored(key) === value) return;
  writeStored(key, value);
  writeStored(atKey, new Date().toISOString());
};

/**
 * Display name of the property last viewed, beside its slug.
 *
 * The slug alone means a lookup just to render "you viewed X" — for a string
 * the browser already had. Written from the property page.
 */
export const setLastVisitedPropertyNameForPostHog = (name?: string): void => {
  if (typeof window === "undefined") return;
  const value = String(name || "").trim();
  if (!value) return;
  writeStored(LAST_VISITED_PROPERTY_NAME_STORAGE_KEY, value);
};

/** The region's display name, beside the slug the route records. */
export const setLastSearchedRegionNameForPostHog = (name?: string): void => {
  if (typeof window === "undefined") return;
  const value = String(name || "").trim();
  if (!value) return;
  writeStored(LAST_SEARCHED_REGION_NAME_STORAGE_KEY, value);
};

/**
 * The filter the user last applied on search.
 *
 * Recorded centrally from the event emitter rather than in each filter
 * component — there are eight of them, and they would drift.
 */
export const setLastAppliedFilterForPostHog = (filterAction?: string): void => {
  if (typeof window === "undefined") return;
  const value = String(filterAction || "").trim();
  if (!value) return;
  writeStored(LAST_APPLIED_FILTER_STORAGE_KEY, value);
  // Stamped every time, not only when the filter changes: "filtered in the last
  // N days" is about when they last did it, not about which one it was.
  writeStored(LAST_APPLIED_FILTER_AT_STORAGE_KEY, new Date().toISOString());
};

/**
 * How stale `last_active_at` is allowed to get.
 *
 * Every stamp is a person-row write on PostHog's side, so writing one per page
 * view or per event would be a large amount of traffic for no gain: Engage's
 * "last active" audiences are cut in hours and days, never minutes.
 */
const LAST_ACTIVE_THROTTLE_MS = 15 * 60 * 1000;

/**
 * Mark this person as active now, at most once per throttle window.
 *
 * Engage cannot use PostHog's own `persons.last_seen_at` for this. That column
 * moves when the person row is written, not when the person does something, so
 * an app session that only fires events leaves it untouched — measured over 3
 * days it reported 3,585 active people where the events table reported 7,084.
 * Reading it would have quietly halved every "active in the last N" audience,
 * so Engage kept scanning the whole events table instead, which was the
 * slowest query it ran.
 *
 * Stamping our own property fixes both ends: Engage answers from one person
 * column, and the value means what its name says.
 *
 * Returns whether the stamp actually moved, so the caller can decide whether a
 * flush to PostHog is worth making.
 */
export const touchLastActiveForPostHog = (): boolean => {
  if (typeof window === "undefined") return false;
  const now = Date.now();
  const previous = readStored(LAST_ACTIVE_AT_STORAGE_KEY);
  if (previous) {
    const last = new Date(previous).getTime();
    if (!Number.isNaN(last) && now - last < LAST_ACTIVE_THROTTLE_MS) return false;
  }
  writeStored(LAST_ACTIVE_AT_STORAGE_KEY, new Date(now).toISOString());
  return true;
};

/** Last property detail page opened, persisted from the route. */
export const setLastVisitedPropertyForPostHog = (canonicalName?: string): void =>
  setTimestampedValue(
    LAST_VISITED_PROPERTY_STORAGE_KEY,
    LAST_VISITED_PROPERTY_AT_STORAGE_KEY,
    String(canonicalName || "").trim(),
  );

/** Last search region opened, persisted from the route. */
export const setLastSearchedRegionForPostHog = (regionSlug?: string): void =>
  setTimestampedValue(
    LAST_SEARCHED_REGION_STORAGE_KEY,
    LAST_SEARCHED_REGION_AT_STORAGE_KEY,
    String(regionSlug || "").trim(),
  );

/**
 * Whether the user currently holds at least one shortlisted property.
 *
 * Deliberately live state and not a sticky "ever" flag: a shortlist
 * notification is only sendable while the property is still on their list.
 * Someone who saved one and then removed it should stop receiving them, so
 * `false` here is a real answer, not a gap to fill from history.
 */
export const setHasShortlistedForPostHog = (hasShortlist: boolean): void => {
  if (typeof window === "undefined") return;
  writeStored(HAS_SHORTLISTED_STORAGE_KEY, hasShortlist ? "true" : "false");
};

/**
 * MOST RECENT time the lead form was opened.
 *
 * Deliberately overwritten rather than written once. Engage answers "opened the
 * lead form in the last N days" by comparing this against now, and only the
 * latest open can do that: with the first open stored, someone who opened it a
 * year ago and again today would look like a year-old lead. Presence still
 * answers the unwindowed "has opened it at all".
 */
export const setLeadFormOpenedForPostHog = (): void => {
  if (typeof window === "undefined") return;
  writeStored(LEAD_FORM_OPENED_AT_STORAGE_KEY, new Date().toISOString());
};

/** The name already recorded for this slug, for forms whose inventory has none. */
export const getRecordedPropertyNameForPostHog = (canonicalName?: string): string => {
  if (typeof window === "undefined") return "";
  const slug = String(canonicalName || "").trim();
  if (!slug || readStored(LAST_VISITED_PROPERTY_STORAGE_KEY) !== slug) return "";
  return readStored(LAST_VISITED_PROPERTY_NAME_STORAGE_KEY) || "";
};

/**
 * Record what the current route says about the user, ready for the next
 * `registerPostHogSuperProperties`. Only the two routes that name a property or
 * a region carry anything.
 */
export const setRouteContextForPostHog = (
  screen?: string,
  routeParams?: Record<string, any>,
): void => {
  const slug = String(routeParams?.id || "").trim();
  if (!slug) return;
  if (screen === APP_PAGE.PROPERTY) setLastVisitedPropertyForPostHog(slug);
  else if (screen === APP_PAGE.SEARCH) setLastSearchedRegionForPostHog(slug);
};

/** PostHog person property → the localStorage key holding it. Engage reads these names. */
const BEHAVIOURAL_PERSON_PROPERTY_KEYS = {
  last_visited_property: LAST_VISITED_PROPERTY_STORAGE_KEY,
  last_visited_property_at: LAST_VISITED_PROPERTY_AT_STORAGE_KEY,
  last_searched_region: LAST_SEARCHED_REGION_STORAGE_KEY,
  last_searched_region_at: LAST_SEARCHED_REGION_AT_STORAGE_KEY,
  has_shortlisted: HAS_SHORTLISTED_STORAGE_KEY,
  lead_form_opened_at: LEAD_FORM_OPENED_AT_STORAGE_KEY,
  last_visited_property_name: LAST_VISITED_PROPERTY_NAME_STORAGE_KEY,
  last_searched_region_name: LAST_SEARCHED_REGION_NAME_STORAGE_KEY,
  last_applied_filter: LAST_APPLIED_FILTER_STORAGE_KEY,
  last_applied_filter_at: LAST_APPLIED_FILTER_AT_STORAGE_KEY,
  last_active_at: LAST_ACTIVE_AT_STORAGE_KEY,
} as const;

/**
 * Behavioural facts Engage segments and push templates read off the person:
 * the property last viewed, the region last searched, whether a shortlist is
 * held. Person properties only — registering them as super properties would
 * put them on every event for no benefit.
 *
 * Engage used to derive the first two by scanning 30 days of `$current_url` for
 * every push-reachable person, a query that times out often enough that
 * workflows disabled it and shipped blank deep links.
 */
export const getBehaviouralPersonProperties = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  return Object.entries(BEHAVIOURAL_PERSON_PROPERTY_KEYS).reduce<Record<string, string>>(
    (props, [name, storageKey]) => {
      const value = readStored(storageKey);
      return value ? { ...props, [name]: value } : props;
    },
    {},
  );
};

/**
 * Drop the behavioural state that belongs to the account rather than the
 * browser, on logout.
 *
 * `posthogReset` starts a fresh anonymous person on this device, and the next
 * `registerPostHogSuperProperties` flushes whatever is in storage onto it. Most
 * of these keys are properties of the browser — the same human is still looking
 * at the same properties — so they stay.
 *
 * `has_shortlisted` is not: a shortlist belongs to the signed-in account, and a
 * signed-out browser cannot see it. Left in place it would claim a shortlist
 * that the new anonymous person does not have, and Engage would send shortlist
 * notifications to someone with nothing shortlisted.
 */
export const clearAccountScopedPostHogProperties = (): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(HAS_SHORTLISTED_STORAGE_KEY);
  } catch {
    // Storage blocked — nothing was stored to begin with.
  }
};

/** Shared event properties for PostHog captures (includes is_app when in app WebView). */
export const getPostHogEventContext = (): Record<string, string | boolean> => {
  const inApp = isApp();
  const userCountry = getUserCountryForPostHog();
  const countryCode = getDestinationCountryForPostHog();

  return {
    ...(inApp && {
      is_app: true,
      ...(appOS ? { app_platform: appOS } : {}),
    }),
    ...(config.IS_CHINA_APP && !inApp && { china_app: true }),
    ...(config.IS_WHITELABEL_APP && { is_whitelabel: true }),
    ...(userCountry && { user_source_country: userCountry }),
    ...(countryCode && { user_destination_country: countryCode }),
  };
};

/** Client-side PostHog is enabled for web and app WebView (not SSR). */
export const isPostHogEnabled = (): boolean => typeof window !== "undefined";

const POSTHOG_CONFIG = getPostHogConfig();

export default POSTHOG_CONFIG;
